import React from "react";
import CustomEllipsis from "../../Utils/CustomEllipsis";
import "./Style/RecentlyViewCard.scss";
import moment from "moment";
import { getReportTypeLabel, redirectToReport, renderIcon } from "./Helper";
import { useHistory, useLocation } from "react-router-dom";
import { OpenSearchService } from "./Service/OpenSearchService";
import constants from "../../Utils/constants";
import Toast from "../../Utils/Toast";
import { HistoricalFileService } from "../../Services/HistoricalFiles";
import { delay } from "lodash";
import gaEvent from "../../Utils/GAEvents";

// Component to render a list of recently viewed items
const RecentlyViewedData = ({ items }) => {
  const history = useHistory();
  const location = useLocation();

  // handler for downloading historical file
  const handleDownloadFile = async (data) => {
    // Step 1: Prepare payload for access check
    const accessPayload = {
      resource_type: data?.type,
      mapping_id: data?.mapping_id || data?.report_id,
      segment_id: data?.segment_id || null,
    };

    // Step 2: Call API to check access and download historical file

    await OpenSearchService.checkResourceAccess(accessPayload)
      .then((res) => {
        const hasFileAccess = res?.data?.has_access;
        const uuid = data.file_uuid;
        if (hasFileAccess) {
          HistoricalFileService.downloadHistoricalFile(uuid)
            .then((response) => {
              const type = response.headers["content-type"];
              // Create a blob from the response
              const blob = new Blob([response.data], {
                type,
              });

              // Create a temporary URL for the blob
              const url = window.URL.createObjectURL(blob);
              delay(() => {
                // Create a link element
                const link = document.createElement("a");
                link.href = url;

                // Set the file name (if provided in response headers)
                let filename = response.headers["content-disposition"]
                  ? response.headers["content-disposition"]
                      .split("filename=")[1]
                      .trim()
                      .replace(/['"]/g, "")
                  : "downloaded_file";

                filename = filename.replace(
                  constants.REG_EX.HISTORICAL_FILE_NAME_REG_EX,
                  ""
                );

                link.setAttribute("download", filename);

                // Append the link to the document
                document.body.appendChild(link);

                // Trigger the download
                link.click();

                // for GA EVENT
                gaEvent.sendEventToGA(
                  constants.GA.CUSTOM_EVENTS.DOWNLOAD_VIEW,
                  {
                    fileName: filename,
                    fileFormat: type,
                    moduleName:
                      constants.APPLICATION_ROUTE.HISTORICAL_FILE.NAME,
                    id: uuid,
                    pageUrl: location.pathname + location.search,
                  }
                );

                // Clean up by removing the link and revoking the object URL
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
              }, 500);
            })
            .catch((error) => {
              Toast(
                error?.data?.error
                  ? error.data.error
                  : constants.ERROR.SOMETHING_WENT_WRONG,
                "error"
              );
            });
        } else {
          // If no access, show info toast
          Toast("You don't have access to this report.", "INFO");
        }
      })
      .catch((error) => {
        Toast(
          error?.data?.error
            ? error.data.error
            : constants.ERROR.SOMETHING_WENT_WRONG,
          "error"
        );
      });
  };
  /**
   * Checks if the user has access to a specific report file and redirects if access is granted.
   * @param {Object} item - Contains report details like type and mapping ID.
   */
  const checkFileAccess = (item) => {
    let accessPayload = {
      resource_type: item?.type,
    };
    if (item.type === "company") {
      accessPayload.mapping_id = item?.company_id;
      accessPayload.segment_id = item?.segment_id;
    } else {
      accessPayload.mapping_id = item?.mapping_id;
    }

    // Step 2: Call API to check access
    OpenSearchService.checkResourceAccess(accessPayload)
      .then((res) => {
        const hasFileAccess = Boolean(res?.data?.has_access);

        if (hasFileAccess) {
          // Step 3: Save recently viewed data
          const recently_viewed_body = {
            ...item,
            subcategory_title:
              item.subcategory_title?.trim() === ""
                ? item.subcategory_name
                : item.subcategory_title,
            published_at: item.created_at,
          };

          return OpenSearchService.saveRecentlyViewedData(recently_viewed_body)
            .then((res) => {
              if (res.status === 200) {
                // Step 4: Redirect to report
                const path = redirectToReport(item);
                window.open(path, "_blank", "noopener,noreferrer");
              }
            })
            .catch((saveError) => {
              console.error("Failed to save recently viewed:", saveError);
            });
        } else {
          // If no access, show info toast
          Toast("You don't have access to this report.", "INFO");
        }
      })
      .catch((error) => {
        // Catch any error in access check flow
        Toast(
          error?.data?.error || constants.ERROR.SOMETHING_WENT_WRONG,
          "error"
        );
      });
  };

  // render upto 5 data
  return items.map((item, idx) => {
    // Destructure properties from item for clarity
    const {
      method,
      type,
      subcategory_title,
      subcategory_name,
      report_name,
      content,
      published_at,
      segment_name,
      files_name,
    } = item;

    // Flags for method type
    const isFile = method === "file";
    const isContent = method === "content";

    // Truncate long texts
    const truncatedSubCatTitle = subcategory_title?.trim()
      ? CustomEllipsis(subcategory_title, 95)
      : CustomEllipsis(subcategory_name, 95);
    const truncatedFileName = CustomEllipsis(files_name, 95);
    const truncatedContent = CustomEllipsis(content, 95);

    return (
      <div className="recent-view-card" key={idx}>
        {/* Icon based on content type */}
        <div className="icon-section">{renderIcon(type, method)}</div>

        <div className="ml-2 info-section">
          {/* Title based on method type */}
          <p
            className={`title ${isFile ? "no-hover" : ""}`}
            onClick={() => {
              if (isContent) checkFileAccess(item);
            }}
          >
            {isContent ? truncatedSubCatTitle.text : truncatedFileName.text}
          </p>

          {/* Description and action row */}
          <div className="desc-download">
            <p
              className={`description ${isFile ? "no-hover" : ""}`}
              onClick={() => {
                if (isContent) checkFileAccess(item);
              }}
            >
              {truncatedContent.text}
            </p>

            {/* Show Download for files, Date for others */}
            {isFile ? (
              <span
                className="download"
                onClick={() => handleDownloadFile(item)}
              >
                Download
              </span>
            ) : (
              <span className="date">
                {published_at
                  ? moment(published_at).format("MMM D, YYYY")
                  : "—"}
              </span>
            )}
          </div>

          {/* Breadcrumb path info */}
          <p className="path">
            {type === "company"
              ? `Company Research > ${report_name} > ${segment_name}`
              : `${getReportTypeLabel(type)} > ${report_name}`}
          </p>
        </div>
      </div>
    );
  });
};

export default RecentlyViewedData;
